import React, { createContext, useState, useContext, useEffect } from 'react';

const ChatHistoryContext = createContext();

export const useChatHistory = () => {
  const context = useContext(ChatHistoryContext);
  if (!context) {
    throw new Error('useChatHistory must be used within ChatHistoryProvider');
  }
  return context;
};

export const ChatHistoryProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // Load conversations from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('chatConversations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConversations(parsed);
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    }
  }, []);

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem('chatConversations', JSON.stringify(conversations));
    }
  }, [conversations]);

  // Generate conversation title from first message
  const generateTitle = (firstMessage) => {
    if (!firstMessage) return 'New conversation';

    // Truncate if too long
    const maxLength = 50;
    if (firstMessage.length > maxLength) {
      return firstMessage.substring(0, maxLength) + '...';
    }
    return firstMessage;
  };

  // Create new conversation
  const createConversation = (firstMessage) => {
    const newConversation = {
      id: Date.now().toString(),
      title: generateTitle(firstMessage),
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversationId(newConversation.id);
    return newConversation.id;
  };

  // Add message to current conversation
  const addMessage = (conversationId, message) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          messages: [...conv.messages, message],
          updatedAt: new Date().toISOString()
        };
      }
      return conv;
    }));
  };

  // Get current conversation
  const getCurrentConversation = () => {
    return conversations.find(conv => conv.id === currentConversationId);
  };

  // Delete conversation
  const deleteConversation = (conversationId) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
    }
  };

  // Start new conversation
  const startNewConversation = () => {
    setCurrentConversationId(null);
  };

  const value = {
    conversations,
    currentConversationId,
    setCurrentConversationId,
    createConversation,
    addMessage,
    getCurrentConversation,
    deleteConversation,
    startNewConversation
  };

  return (
    <ChatHistoryContext.Provider value={value}>
      {children}
    </ChatHistoryContext.Provider>
  );
};
